/**
 * Channel post widgets (betup-server-api docs/feed-post-widgets.md): a channel post's text carries fenced JSON blocks
 * on their own lines. This is the app's lenient parser (betup-ios-new `lib/models/post_widgets.dart`) ported as is: the
 * server validates on write, the client never fails on a post — an unknown `widgetType`, a malformed body or an
 * unclosed fence is skipped (an unclosed fence is read as text).
 */

export type FormResult = 'W' | 'D' | 'L';

export interface ChartItem { label: string; value: number; highlight: boolean }
export interface LineSeries { name: string; values: number[] }
export interface FormRow { label: string; results: FormResult[] }
export interface ComparisonRow { label: string; home: number; away: number; unit: string | null }

export type PostWidget =
    | { widgetType: 'image'; url: string }
    | { widgetType: 'barchart'; title: string | null; unit: string | null; items: ChartItem[] }
    | { widgetType: 'linechart'; title: string | null; unit: string | null; labels: string[]; series: LineSeries[] }
    | { widgetType: 'piechart'; title: string | null; unit: string | null; items: ChartItem[] }
    | { widgetType: 'match'; matchId: number }
    | { widgetType: 'crowd'; matchId: number }
    | { widgetType: 'player'; userId: number }
    | { widgetType: 'team'; teamId: number }
    | { widgetType: 'form'; title: string | null; rows: FormRow[] }
    | { widgetType: 'comparison'; title: string | null; home: string; away: string; rows: ComparisonRow[] }
    | { widgetType: 'stat'; value: string; label: string; caption: string | null }
    | { widgetType: 'table'; title: string | null; columns: string[]; rows: string[][]; highlight: number[] }
    | { widgetType: 'quote'; text: string; source: string | null };

export type PostSegment = { kind: 'text'; text: string } | { kind: 'widget'; widget: PostWidget };

/** Characters of a post's text a collapsed card shows (the app's `feedCardTextLimit`). */
export const CARD_TEXT_LIMIT = 256;

class Malformed extends Error {}

const fail = (what: string): never => { throw new Malformed(what); };

function str(raw: unknown): string | null {
    if (raw == null) return null;
    if (typeof raw !== 'string') return fail('string');
    const value = raw.trim();
    return value === '' ? null : value;
}

function id(raw: unknown): number {
    return typeof raw === 'number' && Number.isInteger(raw) && raw > 0 ? raw : fail('id');
}

function num(raw: unknown): number {
    return typeof raw === 'number' && Number.isFinite(raw) ? raw : fail('number');
}

function list(raw: unknown, min: number, max: number): unknown[] {
    if (!Array.isArray(raw) || raw.length < min) return fail('list');
    return raw.slice(0, max);
}

function obj(raw: unknown): Record<string, unknown> {
    return raw != null && typeof raw === 'object' && !Array.isArray(raw) ? raw as Record<string, unknown> : fail('object');
}

function items(raw: unknown, min: number, max: number): ChartItem[] {
    return list(raw, min, max).map(item => {
        const o = obj(item);
        return { label: str(o.label) ?? '', value: Math.max(0, num(o.value)), highlight: o.highlight === true };
    });
}

/** The block's JSON body as a widget; null when it isn't one the dashboard draws. */
export function widgetFromJson(json: Record<string, unknown>): PostWidget | null {
    try {
        switch (json.widgetType) {
            case 'image': {
                const url = str(json.url);
                return url ? { widgetType: 'image', url } : null;
            }
            case 'barchart':
                return { widgetType: 'barchart', title: str(json.title), unit: str(json.unit), items: items(json.items, 1, 12) };
            case 'linechart': {
                if (!Array.isArray(json.labels) || !Array.isArray(json.series)) return null;
                const labels = json.labels.map(l => str(l) ?? '');
                if (labels.length < 2 || labels.length > 20) return null;
                const series = json.series.slice(0, 3).map(s => {
                    const o = obj(s);
                    if (!Array.isArray(o.values) || o.values.length !== labels.length) return fail('values');
                    return { name: str(o.name) ?? '', values: o.values.map(num) };
                });
                if (series.length === 0) return null;
                return { widgetType: 'linechart', title: str(json.title), unit: str(json.unit), labels, series };
            }
            case 'piechart': {
                const chartItems = items(json.items, 2, 6);
                if (chartItems.reduce((t, i) => t + i.value, 0) <= 0) return null;
                return { widgetType: 'piechart', title: str(json.title), unit: str(json.unit), items: chartItems };
            }
            case 'match': return { widgetType: 'match', matchId: id(json.matchId) };
            case 'crowd': return { widgetType: 'crowd', matchId: id(json.matchId) };
            case 'player': return { widgetType: 'player', userId: id(json.userId) };
            case 'team': return { widgetType: 'team', teamId: id(json.teamId) };
            case 'form':
                return {
                    widgetType: 'form', title: str(json.title),
                    rows: list(json.rows, 1, 4).map(row => {
                        const o = obj(row);
                        return {
                            label: str(o.label) ?? '',
                            results: list(o.results, 1, 10).map(r => (r === 'W' || r === 'D' || r === 'L' ? r : fail('result'))),
                        };
                    }),
                };
            case 'comparison':
                return {
                    widgetType: 'comparison', title: str(json.title), home: str(json.home) ?? '', away: str(json.away) ?? '',
                    rows: list(json.rows, 1, 10).map(row => {
                        const o = obj(row);
                        return { label: str(o.label) ?? '', home: Math.max(0, num(o.home)), away: Math.max(0, num(o.away)), unit: str(o.unit) };
                    }),
                };
            case 'stat': {
                const value = str(json.value);
                return value ? { widgetType: 'stat', value, label: str(json.label) ?? '', caption: str(json.caption) } : null;
            }
            case 'table': {
                const columns = list(json.columns, 2, 6).map(c => str(c) ?? '');
                const rows = list(json.rows, 1, 10).map(row => {
                    if (!Array.isArray(row) || row.length !== columns.length) return fail('row');
                    return row.map(cell => (typeof cell === 'string' ? cell.trim() : String(cell)));
                });
                const highlight = Array.isArray(json.highlight)
                    ? json.highlight.filter((i): i is number => Number.isInteger(i) && i >= 0 && i < rows.length)
                    : [];
                return { widgetType: 'table', title: str(json.title), columns, rows, highlight };
            }
            case 'quote': {
                const text = str(json.text);
                return text ? { widgetType: 'quote', text, source: str(json.source) } : null;
            }
            default:
                return null;
        }
    } catch (e) {
        if (e instanceof Malformed) return null;
        throw e;
    }
}

const OPENING_FENCE = /^```(json)?$/i;
const ONE_LINE_BLOCK = /^```\s*(\{.*\})\s*```$/;

function decode(body: string): PostWidget | null {
    try {
        const json = JSON.parse(body);
        return json != null && typeof json === 'object' && !Array.isArray(json) ? widgetFromJson(json) : null;
    } catch {
        return null;
    }
}

const cache = new Map<string, PostSegment[]>();

/** A channel post's text as paragraphs and widgets, in order (the last 64 texts' results are kept). */
export function parsePostText(text: string): PostSegment[] {
    const cached = cache.get(text);
    if (cached) return cached;
    const segments: PostSegment[] = [];
    const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
    const buffer: string[] = [];
    const flush = () => {
        const paragraph = buffer.join('\n').trim();
        if (paragraph) segments.push({ kind: 'text', text: paragraph });
        buffer.length = 0;
    };
    let i = 0;
    while (i < lines.length) {
        const line = lines[i].trim();
        const oneLine = ONE_LINE_BLOCK.exec(line);
        if (oneLine) {
            flush();
            const widget = decode(oneLine[1]);
            if (widget) segments.push({ kind: 'widget', widget });
            i++;
            continue;
        }
        if (OPENING_FENCE.test(line)) {
            let close = -1;
            for (let j = i + 1; j < lines.length; j++) if (lines[j].trim() === '```') { close = j; break; }
            if (close !== -1) {
                flush();
                const widget = decode(lines.slice(i + 1, close).join('\n'));
                if (widget) segments.push({ kind: 'widget', widget });
                i = close + 1;
                continue;
            }
        }
        buffer.push(lines[i]);
        i++;
    }
    flush();
    cache.set(text, segments);
    if (cache.size > 64) cache.delete(cache.keys().next().value as string);
    return segments;
}

/** The text without its widgets: the paragraphs joined by a blank line (the app's `postPlainText`). */
export function postPlainText(text: string): string {
    return parsePostText(text).filter((s): s is { kind: 'text'; text: string } => s.kind === 'text').map(s => s.text).join('\n\n');
}

export function widgetsOf(text: string): PostWidget[] {
    return parsePostText(text).flatMap(s => (s.kind === 'widget' ? [s.widget] : []));
}

/**
 * The collapsed card's text, as the app shows it: widgets left out, cut at {@link CARD_TEXT_LIMIT} characters;
 * `heldBack` when anything is kept for the full article (longer text or any widget).
 */
export function cardPreview(text: string): { text: string; heldBack: boolean; widgets: number } {
    const plain = postPlainText(text);
    const chars = Array.from(plain);
    const widgets = widgetsOf(text).length;
    const cut = chars.length > CARD_TEXT_LIMIT;
    return {
        text: cut ? chars.slice(0, CARD_TEXT_LIMIT).join('').trimEnd() : plain,
        heldBack: cut || widgets > 0,
        widgets,
    };
}

/** The ids every live widget of [texts] names, per kind (one widget-data call per article). */
export function liveWidgetIds(texts: string[]): { matchIds: number[]; userIds: number[]; teamIds: number[] } {
    const matches = new Set<number>();
    const users = new Set<number>();
    const teams = new Set<number>();
    texts.forEach(text => widgetsOf(text).forEach(w => {
        if (w.widgetType === 'match' || w.widgetType === 'crowd') matches.add(w.matchId);
        if (w.widgetType === 'player') users.add(w.userId);
        if (w.widgetType === 'team') teams.add(w.teamId);
    }));
    return { matchIds: Array.from(matches), userIds: Array.from(users), teamIds: Array.from(teams) };
}

export const WIDGET_LABELS: Record<PostWidget['widgetType'], string> = {
    image: 'Image', barchart: 'Bar chart', linechart: 'Line chart', piechart: 'Pie chart', match: 'Match',
    crowd: 'Crowd', player: 'Player', team: 'Team', form: 'Form', comparison: 'Comparison', stat: 'Stat',
    table: 'Table', quote: 'Quote',
};
